import React from "react";
import { Check, X } from "lucide-react";
import { get } from "lodash";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AccountsTableActions } from "routes/constant_contact/components/accounts_table/AccountsTableActions";

const AUTH_STATUS_CONFIG = {
  authenticated: { label: "Authenticated", variant: "success" },
  pending: { label: "Pending", variant: "secondary" },
  unauthenticated: { label: "Unauthenticated", variant: "outline" },
  expired: { label: "Refresh Failed", variant: "destructive" },
};

// A refresh failure doesn't always flip the status away from `authenticated`
// (e.g. a transient 429 or 5xx) — the reason is still surfaced, so the badge
// has to reflect it rather than the raw status alone.
const REFRESH_ISSUE_CONFIG = { label: "Refresh Issue", variant: "warning" };

export const getAccountsTableColumns = () => {
  return [
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <p style={{ fontWeight: "bold" }}>{row.original?.id}</p>
      ),
    },
    {
      header: "Email Address",
      id: "Email Address",
      accessorKey: "email_address",
    },
    {
      header: "Active",
      id: "Active",
      accessorKey: "active",
      cell: ({ row }) =>
        row.original?.active ? (
          <Check className="tw:size-4 tw:text-success" aria-label="Active" />
        ) : (
          <X className="tw:size-4 tw:text-destructive" aria-label="Inactive" />
        ),
    },
    {
      header: "Authentication Status",
      id: "Authentication Status",
      accessorKey: "authentication_status",
      cell: ({ row }) => {
        const status = get(row, "original.authentication_status");
        const error = row.original?.last_refresh_error;
        const config =
          error && status === "authenticated"
            ? REFRESH_ISSUE_CONFIG
            : (AUTH_STATUS_CONFIG[status] ?? {
                label: (status ?? "N/A").toUpperCase(),
                variant: "outline",
              });
        const badge = <Badge variant={config.variant}>{config.label}</Badge>;

        if (!error) {
          return badge;
        }

        return (
          <Tooltip>
            <TooltipTrigger
              aria-label={error}
              className="tw:cursor-help tw:border-0 tw:bg-transparent tw:p-0"
            >
              {badge}
            </TooltipTrigger>
            <TooltipContent>{error}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => <AccountsTableActions original={row.original} />,
    },
  ];
};
